'use client';

import * as React from 'react';
import { ExpandIcon, XIcon } from 'lucide-react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger
} from '@workspace/ui/components/dialog';
import { cn } from '@workspace/ui/lib/utils';

export type DocsImageProps = React.ImgHTMLAttributes<HTMLImageElement>;

/**
 * Renders a documentation image as an inline thumbnail that opens full screen
 * in a dialog when clicked. Used as the MDX `img` renderer, so every docs
 * image becomes zoomable without changing any content files.
 */
export function DocsImage({
  className,
  alt,
  src,
  ...props
}: DocsImageProps): React.JSX.Element {
  const label = typeof alt === 'string' && alt.length > 0 ? alt : 'Documentation image';

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`View full screen: ${label}`}
          className={cn(
            'group/zoom relative mt-4 block w-full cursor-zoom-in overflow-hidden rounded-lg border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className
          )}
        >
          <img
            className="w-full"
            alt={alt}
            src={src}
            {...props}
          />
          <span className="pointer-events-none absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background/80 px-2 py-1 text-xs font-medium text-foreground opacity-0 shadow-sm backdrop-blur transition-opacity group-hover/zoom:opacity-100">
            <ExpandIcon className="size-3.5" />
            Click to enlarge
          </span>
        </button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className="left-0 top-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0 items-center justify-center gap-0 rounded-none border-none bg-black/90 p-4 shadow-none sm:max-w-none"
      >
        <DialogTitle className="sr-only">{label}</DialogTitle>
        {/* Click the backdrop (anywhere outside the image) to close */}
        <DialogClose
          aria-label="Close full screen image"
          className="absolute inset-0 cursor-zoom-out focus:outline-none"
        />
        <img
          className="relative z-10 max-h-full max-w-full object-contain"
          alt={alt}
          src={src}
        />
        <DialogClose
          aria-label="Close full screen image"
          className="absolute right-4 top-4 z-20 flex size-10 cursor-pointer items-center justify-center rounded-full bg-background/90 text-foreground shadow-md ring-1 ring-border backdrop-blur transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <XIcon className="size-5" />
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
